import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Dropdown.css';

/**
 * A select that stays inside the page.
 *
 * A native <select> hands the choice to the operating system, which on a phone
 * means a grey sheet sliding up from the bottom in the system's own colours —
 * the site disappears at the moment somebody is choosing a course. This draws
 * the list itself, in the brand palette, wherever it is used.
 *
 * Giving up the native control means taking on what it did for free, so:
 * arrow keys move the highlight, Home and End jump, Enter and Space choose,
 * Escape closes and returns focus to the trigger, typing a letter jumps to the
 * next option starting with it, clicking away closes, and the whole thing is
 * announced as a listbox. A long list scrolls rather than growing off-screen.
 *
 * Options are { value, label }. `value` is compared as a string, so numeric
 * ids from the API work without the caller converting them.
 */
export default function Dropdown({ value, onChange, options, placeholder = '—', disabled, id, className = '' }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);
  const typed = useRef({ text: '', at: 0 });

  const selectedIndex = options.findIndex((o) => String(o.value) === String(value ?? ''));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const close = useCallback((refocus) => {
    setOpen(false);
    setActive(-1);
    if (refocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  // Clicking or tapping anywhere else puts the list away, the way a native
  // one behaves.
  useEffect(() => {
    if (!open) return undefined;
    const onDocument = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close(false);
    };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('touchstart', onDocument);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('touchstart', onDocument);
    };
  }, [open, close]);

  // Keep the highlighted row in view when moving through a long list.
  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const row = listRef.current.children[active];
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const choose = (index) => {
    const option = options[index];
    if (!option) return;
    onChange(String(option.value));
    close(true);
  };

  const openWith = (index) => {
    setOpen(true);
    setActive(index);
  };

  const onKeyDown = (e) => {
    if (disabled) return;

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openWith(selectedIndex >= 0 ? selectedIndex : 0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(active);
        break;
      case 'Tab':
        close(false);
        break;
      default:
        // Type-ahead: letters typed in quick succession search together, so
        // "ma" finds Mathematics rather than stopping at the first M.
        if (e.key.length === 1) {
          const now = Date.now();
          typed.current.text = now - typed.current.at > 700 ? e.key : typed.current.text + e.key;
          typed.current.at = now;
          const needle = typed.current.text.toLowerCase();
          const found = options.findIndex((o) => String(o.label).toLowerCase().startsWith(needle));
          if (found >= 0) setActive(found);
        }
    }
  };

  return (
    <div ref={rootRef} className={`dd ${className}`.trim()}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`dd-trigger${open ? ' open' : ''}${selected ? '' : ' placeholder'}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close(false) : openWith(selectedIndex >= 0 ? selectedIndex : 0))}
        onKeyDown={onKeyDown}
      >
        <span className="dd-value">{selected ? selected.label : placeholder}</span>
        <svg className="dd-caret" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="dd-list" role="listbox" ref={listRef} tabIndex={-1}>
          {options.map((o, i) => (
            <li
              key={String(o.value)}
              role="option"
              aria-selected={i === selectedIndex}
              className={`dd-option${i === active ? ' active' : ''}${i === selectedIndex ? ' selected' : ''}`}
              // mousedown rather than click: the document listener that closes
              // the list fires first otherwise and the choice never lands.
              onMouseDown={(e) => { e.preventDefault(); choose(i); }}
              onMouseEnter={() => setActive(i)}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
